/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */

import React, { useEffect } from 'react';
import {
    object, bool, string, shape,
} from 'prop-types';
import { connect, useDispatch } from 'react-redux';
import { withRouter } from 'react-router';
import { useQuery } from '@apollo/client';
import { compose } from 'recompose';
import mbpLogger from '@module-logger/mbp';
import mbpUtil from '@api-util/mbp';
import loadable from '@loadable/component';

import { trackModule } from '../../../helpers/tracking/common/commonTrackingHelpers';
import findURL from '../../../gql/queries/findURL';
import { GRAPHQL_ENV } from '../../../gql';
import { getIsBot } from '../../../../state/ducks/App/App-Selectors';
import { getBrand } from '../../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import { getFeatureFlags, getFeatureFlag } from '../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { getSCILoadingState } from '../../../../state/ducks/SCI/SCI-Selectors';
import CoreBrowsingRedirectContainer from './CoreBrowsingRedirectContainer';
import GfAndGnavAbTestContext from './GfAndGnavAbTestContext';
import useSalesforcePersonalizedContentQuery from '../../../helpers/Personalization/useSalesforcePersonalizedContentQuery';
import useClickStreamCustomEventsTracking from '../../../helpers/Personalization/useClickstreamCustomEventsTracking';
import { trackEvent } from '../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import useExperience from '../../../helpers/Personalization/useExperience';

const LoadableCoreBrowsingBuilder = loadable(() => import(/* webpackChunkName: "CoreBrowsingBuilder" */ './CoreBrowsingBuilder'));
const LoadableGraphqlSearchContainer = loadable(() => import(/* webpackChunkName: "GraphqlSearchContainer" */ '../GraphqlSearchPage/GraphqlSearchContainer'));

const CoreBrowsingSearchPath = ({ pathname, brand, ffIsProductListABTestEnabled }) => {
    const dispatch = useDispatch();
    const {
        loading: productListCountLoading, error: productListCountError, data: productListCountData, variables: productListCountVariables,
    } = useExperience({
        queryName: 'ProductListCount',
        interactionName: `${brand.domain}-product-list-count`,
        featureFlag: ffIsProductListABTestEnabled,
        optInHoldoutParticipation: false,
        resolveIdentity: false,
        type: 'experiment',
    });

    if (productListCountError) {
        mbpLogger.logError({
            function: 'SearchPageContainer',
            message: 'Error loading API data from ProductListCount',
            appName: process.env.npm_package_name,
            module: 'PersonalizedContent',
            jsError: productListCountError,
        });
    }

    useClickStreamCustomEventsTracking({
        experimentTracking: productListCountData?.experience?.experimentTracking,
        isFireImpression: true,
        page: { type: 'search' },
    });

    useEffect(() => {
        if (ffIsProductListABTestEnabled && productListCountData?.experience?.experiment?.featureId) {
            dispatch(trackEvent({
                eventCategory: 'Experiment',
                eventAction: 'No Of Collection Products Search Page',
                eventLabel: productListCountData?.experience?.experiment?.name,
                experienceId: productListCountData?.experience?.experimentTracking?.id,
                campaignId: productListCountData?.experience?.experimentTracking?.campaignId,
                variant: productListCountData?.experience?.experimentTracking?.name,
            }));
        }
    }, [productListCountData]);

    const campaignPageSize = productListCountData?.experience?.experiment?.value?.search ?? {};

    const pathnameArr = pathname.split('/');
    const searchTerm = pathnameArr[2];
    return <LoadableGraphqlSearchContainer key={`search-${pathname}`} brand={brand} searchTerm={searchTerm} campaignPageSize={campaignPageSize} campaignLoading={productListCountLoading || productListCountVariables?.skip} />;
};

CoreBrowsingSearchPath.propTypes = {
    pathname: string.isRequired,
    brand: object.isRequired,
    ffIsProductListABTestEnabled: bool.isRequired,
};

/*
    Component Intent
        - This component's job is to take a path, vanity url, or bannercode and begin page determination
        - The priority for findURL is vanity > bannercode > path
        - When the findURL call is made we recieve a type back
        - That type will tell us what page to build and query to run next

        - There should most likely be 2 fallbacks:
            * if a pathname has no type returned, run a Search query
            * if no pathname, or a totally bogus one, revert to HomePage
*/

const CoreBrowsingContainer = ({
    location: { pathname, hash }, brand, ffIsRedirectTestingEnabled, isBot = false, is404, productUnavailableName, SCIIsLoading, whichUrlRedirect,
    ffIsGfAndGnavEdsAbTestEnabled, ffIsGfAndGnavEdsContextEnabled, ffIsProductListABTestEnabled,
}) => {
    // after initial search findURL fails to run again
    // below if statement checks if 'searchterm' exists in url before calling find URL
    // if exists it will call search container
    if (pathname.includes('searchterm')) {
        return <CoreBrowsingSearchPath pathname={pathname} brand={brand} ffIsProductListABTestEnabled={ffIsProductListABTestEnabled} />;
    }
    // AB TEST for CA pricing
    const { data: gfGnavData, loading: gfGnavLoading, variables: gfGnavVariables } = useSalesforcePersonalizedContentQuery({
        queryName: 'GfGnavEds',
        interactionName: `${brand?.domain} - Get Campaign - Gf and Gnav Eds`,
        featureFlag: ffIsGfAndGnavEdsAbTestEnabled, // ffIsPriceBreakDownEnabled
        optInHoldoutParticipation: false,
        resolveIdentity: false,
    });
    const dispatch = useDispatch();
    const gfAndGnavEdsAbTestUserGroup = gfGnavData?.contentSF?.campaign?.campaignResponses?.[0]?.payload?.userGroup;
    const gfAndGnavEdsAbTestVariant = gfAndGnavEdsAbTestUserGroup?.toLowerCase() === 'test';

    const trackClickThrough = useClickStreamCustomEventsTracking({
        salesforceResponse: gfGnavData?.contentSF,
        isFireImpression: true,
        page: 'category',
    });

    useEffect(() => {
        if (gfGnavData && gfAndGnavEdsAbTestUserGroup) {
            const gfAndGnavEdsAbTestValue = sessionStorage.getItem('gfAndGnavEdsAbTest');
            if (!gfAndGnavEdsAbTestValue) {
                dispatch(trackEvent({
                    eventCategory: 'Experiment',
                    eventAction: 'Gf and Gnav Eds',
                    eventLabel: gfAndGnavEdsAbTestVariant ? 'Variant' : 'Control',
                    feature_detail: gfAndGnavEdsAbTestVariant ? 'Variant' : 'Control',
                    feature_element: 'Gf and Gnav Eds',
                    feature_category: 'Experiment',
                }));
                sessionStorage.setItem('gfAndGnavEdsAbTest', true);
            }
        }
    }, [gfGnavData, gfAndGnavEdsAbTestUserGroup]);

    // on page transition, bring user back to top of page - currently not working on back nav when cached
    useEffect(() => {
        if (typeof window !== 'undefined' && hash === '') {
            window.scroll(0, 0);
        }
    }, [pathname]);

    let newPathName;
    if (pathname.includes(' ') || pathname.includes('.')) {
        newPathName = encodeURI(pathname);
    }
    if (pathname.includes("'")) {
        newPathName = encodeURI(pathname).replace(/'/g, '%27');
    }
    if (pathname.includes('&')) {
        newPathName = encodeURI(pathname).replace(/&/g, 'and');
    }

    const FIND_URL_QUERY = findURL(brand.domain, newPathName || pathname);
    const { loading, data, error } = useQuery(FIND_URL_QUERY);

    if (loading && !SCIIsLoading) {
        return null;
    }

    if (error || !data) {
        mbpLogger.logError({
            appName: process.env.npm_package_name,
            query: FIND_URL_QUERY,
            component: 'CoreBrowsingContainer.js',
            message: 'Error loading data from Graphql',
            env: GRAPHQL_ENV,
            error,
        });
        return null;
    }

    const pageData = data.findURL;

    if (mbpUtil.getEnv('REACT_APP_MBP_LOGGER_CONSOLE') === 'DEBUG') {
        console.log('CoreBrowsingContainer pageData', pageData);
    }

    const hasPathRedirectEnabled = () => {
        if (whichUrlRedirect?.includedPathList) {
            const redirectConfig =  (whichUrlRedirect?.includedPathList || [])?.find?.((item) => item?.url === pathname) || null;
            return {
                isRedirectEnabled: !!redirectConfig,
                transition: redirectConfig?.transition || whichUrlRedirect?.transition || 'siteLoader',
            };
        }
        return {
            isRedirectEnabled: pathname === whichUrlRedirect?.url || false,
            transition: whichUrlRedirect?.transition || 'siteLoader',
        };
    };

    const pathRedirectRender = hasPathRedirectEnabled();
    if (ffIsRedirectTestingEnabled
        && !isBot
        && pathRedirectRender.isRedirectEnabled
    ) {
        mbpLogger.logDebug({ appName: process.env.npm_package_name, message: 'CoreBrowsingContainer ... redirecting' });
        return (
            ffIsGfAndGnavEdsContextEnabled
                ? (
                    <GfAndGnavAbTestContext.Provider value={{ gfAndGnavEdsAbTestVariant, gfGnavLoading, gfGnavVariables }}>
                        <CoreBrowsingRedirectContainer
                            pathname={pathname}
                            page={pageData}
                            brand={brand}
                            ffIsRedirectTestingEnabled={ffIsRedirectTestingEnabled}
                            transition={pathRedirectRender.transition}
                        />
                    </GfAndGnavAbTestContext.Provider>
                )
                : (
                    <CoreBrowsingRedirectContainer
                        pathname={pathname}
                        page={pageData}
                        brand={brand}
                        ffIsRedirectTestingEnabled={ffIsRedirectTestingEnabled}
                        transition={pathRedirectRender.transition}
                    />
                )
        );
    }

    mbpLogger.logDebug({ appName: process.env.npm_package_name, message: `CoreBrowsingContainer ... sending to CoreBrowsingBuilder.  is404? ${is404}` });
    return (
        ffIsGfAndGnavEdsContextEnabled
            ? (
                <GfAndGnavAbTestContext.Provider value={{ gfAndGnavEdsAbTestVariant, gfGnavLoading, gfGnavVariables }}>
                    <LoadableCoreBrowsingBuilder
                        brand={brand}
                        pageData={pageData}
                        is404={is404}
                        productUnavailableName={productUnavailableName}
                    />
                </GfAndGnavAbTestContext.Provider>
            )
            : (
                <LoadableCoreBrowsingBuilder
                    brand={brand}
                    pageData={pageData}
                    is404={is404}
                    productUnavailableName={productUnavailableName}
                />
            )
    );
};

CoreBrowsingContainer.propTypes = {
    location: object.isRequired,
    brand: object.isRequired,
    ffIsRedirectTestingEnabled: bool.isRequired,
    ffIsGfAndGnavEdsAbTestEnabled: bool.isRequired,
    ffIsGfAndGnavEdsContextEnabled: bool.isRequired,
    ffIsProductListABTestEnabled: bool.isRequired,
    isBot: bool.isRequired,
    is404: bool,
    productUnavailableName: string,
    SCIIsLoading: bool,
    whichUrlRedirect: shape({
        transition: string,
        url: string,
    }),
};

CoreBrowsingContainer.defaultProps = {
    is404: false,
    productUnavailableName: '',
    SCIIsLoading: false,
    whichUrlRedirect: {
        transition: '',
        url: '',
    },
};

const mapStateToProps = (state) => ({
    brand: getBrand(state),
    featureFlags: getFeatureFlags(state),
    ffIsRedirectTestingEnabled: getFeatureFlag('is-redirect-testing-enabled')(state),
    whichUrlRedirect: getFeatureFlag('which-redirect-test-1-variant')(state),
    ffIsGfAndGnavEdsAbTestEnabled: getFeatureFlag('is-gf-and-gnav-eds-ab-test-enabled')(state),
    ffIsGfAndGnavEdsContextEnabled: getFeatureFlag('is-gf-and-gnav-eds-context-enabled')(state),
    ffIsProductListABTestEnabled: getFeatureFlag('is-product-list-ab-test-enabled')(state),
    isBot: getIsBot(state),
    SCIIsLoading: getSCILoadingState(state),
});

const enhance = compose(
    trackModule('CoreBrowsing'),
    withRouter,
    connect(mapStateToProps),
);

export default enhance(CoreBrowsingContainer);
